import { useEffect, useRef, useState } from 'react';
import { call } from '../api';
import { linkProps } from '../router';

interface Ratings { seasons: { season: number; episodes: { episode: number; title: string; rating: number | null; aired: boolean }[] }[] }

/** Kleur bij een score (zoals TVChart/ARVIO): donkergroen top, groen goed, geel oké, oranje matig, rood slecht. */
export function heatColor(r: number | null): { bg: string; fg: string } {
  if (r === null) return { bg: 'rgba(255,255,255,0.08)', fg: 'var(--c-text-muted)' };
  if (r >= 9) return { bg: '#16804a', fg: '#fff' };
  if (r >= 8) return { bg: '#27b462', fg: '#fff' };
  if (r >= 7.5) return { bg: '#f1cd3c', fg: '#2a2200' };
  if (r >= 7) return { bg: '#f29b1d', fg: '#fff' };
  if (r >= 6) return { bg: '#e2602b', fg: '#fff' };
  return { bg: '#c43a2e', fg: '#fff' };
}

const fmt = (r: number) => (Number.isInteger(r) ? String(r) : r.toFixed(1).replace('.', ','));

// Eén keer ophalen per serie, ook als je van seizoen wisselt
const cache = new Map<number, Promise<Ratings>>();

/**
 * Scores per aflevering van het gekozen seizoen: één rij gekleurde tegels onder de afleveringen.
 * Een tegel opent de aflevering als die in je bibliotheek staat.
 */
export function EpisodeHeatmap({ tmdbId, season, episodeLink }: { tmdbId: number; season: number | null; episodeLink?: (episode: number) => string | null }) {
  const [data, setData] = useState<Ratings | null>(null);
  const track = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: true });

  useEffect(() => {
    setData(null);
    let p = cache.get(tmdbId);
    if (!p) {
      p = call<Ratings>(`/api/meta/tv/${tmdbId}/episode-ratings`);
      cache.set(tmdbId, p);
      p.catch(() => cache.delete(tmdbId));
    }
    p.then(setData).catch(() => setData({ seasons: [] }));
  }, [tmdbId]);

  const s = data?.seasons.find((x) => x.season === season) ?? null;
  const rated = s?.episodes.filter((e) => e.rating !== null) ?? [];

  const measure = () => {
    const el = track.current;
    if (el) setEdge({ start: el.scrollLeft < 4, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4 });
  };
  useEffect(() => {
    if (track.current) track.current.scrollLeft = 0;
    measure();
  }, [s]);

  if (!s || rated.length < 2) return null;
  const avg = Math.round((rated.reduce((a, e) => a + (e.rating ?? 0), 0) / rated.length) * 10) / 10;
  const page = (d: -1 | 1) => track.current?.scrollBy({ left: d * track.current.clientWidth * 0.9, behavior: 'smooth' });

  return (
    <section className="row ep-ratings" aria-label="Scores per aflevering">
      <h2>Scores per aflevering</h2>
      <p className="ep-ratings-sub">Seizoen {s.season} · {s.episodes.length} afleveringen · gemiddeld {fmt(avg)}</p>
      <div className="ep-ratings-rail">
        {!edge.start && <button type="button" className="ep-ratings-nav is-prev" aria-label="Vorige afleveringen" onClick={() => page(-1)}>‹</button>}
        <div className="ep-ratings-track" ref={track} onScroll={measure} data-focus-group>
          {s.episodes.map((e) => {
            const c = heatColor(e.rating);
            const href = episodeLink?.(e.episode) ?? null;
            const label = `Aflevering ${e.episode}${e.title ? `: ${e.title}` : ''}, ${e.rating !== null ? `score ${fmt(e.rating)}` : 'nog geen score'}`;
            const inner = (
              <>
                <span className="ep-tile-ep">E{e.episode}</span>
                <span className="ep-tile-score">{e.rating !== null ? fmt(e.rating) : '–'}</span>
              </>
            );
            const style = { background: c.bg, color: c.fg, opacity: e.aired ? 1 : 0.45 };
            return href ? (
              <a key={e.episode} className="ep-tile" style={style} title={e.title} aria-label={label} {...linkProps(href)}>{inner}</a>
            ) : (
              <span key={e.episode} className="ep-tile" style={style} title={e.title} aria-label={label} role="img">{inner}</span>
            );
          })}
        </div>
        {!edge.end && <button type="button" className="ep-ratings-nav is-next" aria-label="Volgende afleveringen" onClick={() => page(1)}>›</button>}
      </div>
    </section>
  );
}
